/**
 * Socket-id helpers for identify_customer / identify_supplier routes.
 *
 *   "Walmart Inc."    -> "walmart_inc"
 *   "7-Eleven, Inc."  -> "c_7_eleven_inc"  (must start with a letter)
 *   "AT&T"            -> "at_t"
 */

export function slugifyPartyName(name: string, fallback: string): string {
  let slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (slug.length === 0) return fallback;
  if (!/^[a-z]/.test(slug)) {
    const prefix = fallback.charAt(0) || 'x';
    slug = `${prefix}_${slug}`;
  }
  return slug;
}

export function makeUniqueSlug(
  base: string,
  taken: Set<string>,
  reserved: Set<string>,
): string {
  if (!taken.has(base) && !reserved.has(base)) return base;
  let i = 2;
  while (taken.has(`${base}_${i}`) || reserved.has(`${base}_${i}`)) {
    i += 1;
  }
  return `${base}_${i}`;
}

function isCurrentSlugForBase(currentId: string, base: string): boolean {
  if (currentId === base) return true;
  const prefix = `${base}_`;
  if (!currentId.startsWith(prefix)) return false;
  return /^\d+$/.test(currentId.slice(prefix.length));
}

/**
 * Auto-sync a route's socket id + label from the resolved entity name.
 * Returns null when the current id/label already match.
 */
export function nextRouteIdentity(opts: {
  currentId: string;
  currentLabel: string | undefined;
  entityName: string;
  siblingIds: Set<string>;
  reserved: Set<string>;
  slugFallback: string;
}): { id: string; label: string } | null {
  const desiredLabel = opts.entityName;
  const desiredBaseSlug = slugifyPartyName(opts.entityName, opts.slugFallback);
  const needsId =
    opts.currentId.length === 0 ||
    !isCurrentSlugForBase(opts.currentId, desiredBaseSlug);
  const desiredId = needsId
    ? makeUniqueSlug(desiredBaseSlug, opts.siblingIds, opts.reserved)
    : opts.currentId;
  if (desiredId === opts.currentId && desiredLabel === opts.currentLabel) {
    return null;
  }
  return { id: desiredId, label: desiredLabel };
}
